/**
 * Composable singleton du mode maintenance (SD-4367).
 *
 * État module-level partagé par le router, App.vue et les vues : un seul polling
 * (10 s) pour toute l'app. Le statut effectif est recalculé localement (offset
 * horloge serveur + tick 1 s pendant un décompte) : à 0:00 la bascule se fait
 * SANS attendre le prochain poll.
 *
 * Résilience deploy (SD-4352, pas de zero-downtime) : un échec réseau ou une
 * réponse non-200 ne modifie JAMAIS l'état — seul un 200 `status:"off"` éteint
 * le mode. Le dernier état connu est persisté en localStorage pour ré-afficher
 * la page maintenance immédiatement après un rechargement pendant que le
 * backend est down.
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'

import { authFetch, onMaintenanceDetected } from '@/auth/api'
import config from '@/shared/config'

import {
  computeRemainingSeconds,
  computeServerOffsetMs,
  effectiveStatus,
  type MaintenanceStatus,
  type MaintenanceStatusPayload,
} from './logic'
import { STORAGE_RETURN_TO } from './storageKeys'

const STORAGE_LAST = 'sapiendo.maintenance.last'
const POLL_INTERVAL_MS = 10_000

function readStoredPayload(): MaintenanceStatusPayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_LAST)
    return raw ? (JSON.parse(raw) as MaintenanceStatusPayload) : null
  } catch {
    return null
  }
}

function persistPayload(payload: MaintenanceStatusPayload): void {
  try {
    localStorage.setItem(STORAGE_LAST, JSON.stringify(payload))
  } catch {
    /* stockage indisponible (mode privé, quota) : l'état mémoire suffit */
  }
}

const rawPayload: Ref<MaintenanceStatusPayload | null> = ref(readStoredPayload())
const serverOffsetMs = ref(0)
// Tick 1 s pendant un `scheduled` : force la réévaluation du statut effectif et
// du décompte sans aller-retour réseau.
const nowTick = ref(Date.now())

const status: ComputedRef<MaintenanceStatus> = computed(() => {
  const payload = rawPayload.value
  return payload ? effectiveStatus(payload, nowTick.value + serverOffsetMs.value) : 'off'
})

const remainingSeconds: ComputedRef<number | null> = computed(() => {
  const payload = rawPayload.value
  if (!payload || payload.status !== 'scheduled') return null
  return computeRemainingSeconds(payload.activates_at, nowTick.value + serverOffsetMs.value)
})

let tickTimer: ReturnType<typeof setInterval> | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let loadPromise: Promise<void> | null = null
// SD-4692 — source externe du statut (sondage de l'état du service d'un agent) : le shell ne sonde plus lui-même.
let externalUnsubscribe: (() => void) | null = null
let pollingWanted = false

function syncTick(): void {
  const needTick = rawPayload.value?.status === 'scheduled'
  if (needTick && tickTimer === null) {
    tickTimer = setInterval(() => {
      nowTick.value = Date.now()
    }, 1000)
  } else if (!needTick && tickTimer !== null) {
    clearInterval(tickTimer)
    tickTimer = null
  }
}

function applyPayload(payload: MaintenanceStatusPayload): void {
  rawPayload.value = payload
  serverOffsetMs.value = computeServerOffsetMs(payload.server_time, Date.now())
  nowTick.value = Date.now()
  persistPayload(payload)
  syncTick()
}

async function refreshNow(): Promise<void> {
  const response = await authFetch(`${config.api.baseURL}/v1/maintenance/status`, { skipAuth: true })
  // Non-200 (5xx nginx pendant un deploy, etc.) : on CONSERVE le dernier état connu.
  if (!response.ok) return
  applyPayload((await response.json()) as MaintenanceStatusPayload)
}

/** Premier chargement memoizé — ne rejette JAMAIS (un throw dans beforeEach = app blanche). */
function ensureLoaded(): Promise<void> {
  if (!loadPromise) {
    loadPromise = refreshNow().catch(() => undefined)
  }
  return loadPromise
}

function startPolling(): void {
  pollingWanted = true
  if (pollTimer !== null || externalUnsubscribe !== null) return
  void ensureLoaded()
  pollTimer = setInterval(() => {
    void refreshNow().catch(() => undefined)
  }, POLL_INTERVAL_MS)
}

function stopPolling(): void {
  pollingWanted = false
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

/** Un instantané `GET /service/status` : « off » suffit, sinon le détail (échéance) est relu. */
export interface ServiceMaintenanceSnapshot {
  maintenance: 'off' | 'scheduled' | 'active'
  server_time: string | null
}

// Un instantané `null` (`/service/status` illisible : Redis en panne, réponse non conforme) relit l'endpoint de
// maintenance, qui ne dépend pas de Redis : le sondage propre étant suspendu tant que la source est liée, ne rien
// relire laisserait invisible une maintenance déclenchée pendant la panne.
async function syncFromService(snapshot: ServiceMaintenanceSnapshot | null): Promise<void> {
  if (snapshot && snapshot.maintenance === 'off') {
    applyPayload({ status: 'off', activates_at: null, server_time: snapshot.server_time ?? new Date().toISOString() })
    return
  }
  await refreshNow()
}

/**
 * SD-4692 — un seul sondage par onglet : un agent qui sonde déjà `GET /service/status` (voyant) alimente le mode
 * maintenance par cette source ; le sondage propre de `/maintenance/status` est suspendu tant qu'elle est liée.
 * Renvoie la fonction qui délie la source (le sondage propre reprend s'il était demandé).
 */
function bindStatusSource(
  subscribe: (listener: (snapshot: ServiceMaintenanceSnapshot | null) => void) => () => void,
): () => void {
  externalUnsubscribe?.()
  externalUnsubscribe = subscribe((snapshot) => {
    void syncFromService(snapshot).catch(() => undefined)
  })
  if (pollTimer !== null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  return () => {
    externalUnsubscribe?.()
    externalUnsubscribe = null
    if (pollingWanted) startPolling()
  }
}

function saveReturnTo(fullPath: string): void {
  try {
    localStorage.setItem(STORAGE_RETURN_TO, fullPath)
  } catch {
    /* stockage indisponible : on retombera sur la home */
  }
}

function consumeReturnTo(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_RETURN_TO)
    localStorage.removeItem(STORAGE_RETURN_TO)
    return value
  } catch {
    return null
  }
}

/**
 * Événement navigateur émis par la copie GGVie (`agents/ggvie/consulting/auth/api.ts`) quand un de SES appels reçoit
 * le 503-marqueur. Montée dans la sandbox (route /ggvie), elle appelle l'API par son propre module d'auth, dont le
 * bus n'a pas d'abonné ici : sans cet événement, la page ne basculait qu'au sondage suivant (≈ 10 s). Même nom des
 * deux côtés (aucun import croisé, ADR-019) ; le module d'auth du shell n'émet pas cet événement, son bus suffit.
 */
export const MAINTENANCE_DETECTED_DOM_EVENT = 'sapiendo:maintenance-detected'

// Bascule immédiate quand un appel API quelconque reçoit le 503-marqueur : on force
// `active` localement puis on resynchronise avec l'endpoint de vérité.
function forceActive(): void {
  const forced: MaintenanceStatusPayload = {
    status: 'active',
    activates_at: null,
    server_time: new Date(Date.now() + serverOffsetMs.value).toISOString(),
  }
  rawPayload.value = forced
  persistPayload(forced)
  syncTick()
  void refreshNow().catch(() => undefined)
}

onMaintenanceDetected(forceActive)
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener(MAINTENANCE_DETECTED_DOM_EVENT, forceActive)
}

// Rechargement pendant un décompte hydraté du localStorage : relance le tick.
syncTick()

export interface UseMaintenance {
  status: ComputedRef<MaintenanceStatus>
  remainingSeconds: ComputedRef<number | null>
  ensureLoaded: () => Promise<void>
  startPolling: () => void
  stopPolling: () => void
  refreshNow: () => Promise<void>
  saveReturnTo: (fullPath: string) => void
  consumeReturnTo: () => string | null
  bindStatusSource: (
    subscribe: (listener: (snapshot: ServiceMaintenanceSnapshot | null) => void) => () => void,
  ) => () => void
}

const singleton: UseMaintenance = {
  status,
  remainingSeconds,
  ensureLoaded,
  startPolling,
  stopPolling,
  refreshNow,
  saveReturnTo,
  consumeReturnTo,
  bindStatusSource,
}

export function useMaintenance(): UseMaintenance {
  return singleton
}
